import { useQuery } from '@apollo/client'
import _ from 'lodash'
import { useMemo } from 'react'

import useConfig from '../../hooks/useLossPreventionConfig'
import useStoreList from '../../hooks/useStoreList'
import {
  IListLocationLossPreventionKpisNodeType,
  IListLocationLossPreventionKpisType,
  ILossPreventionItems,
  LIST_LOCATION_LOSS_PREVENTION_KPIS,
  LIST_LOCATION_METRIC_VALUES,
} from '../gql'

interface ICustomDataType {
  [key: string]: {
    count: number
    amount: number
    checkPercent: number | { value: number; color: string }
  }
}

const useLocationCash = (
  locationIds?: number[],
  locationGroupIds?: number[],
  startDate?: string,
  endDate?: string,
) => {
  const config = useConfig()
  const getColorFromValue = config?.cashControlTable?.getColorFromValue
  const metrics = useMemo(
    () =>
      Object.values(config.cashControlTable.cashControlDataFromMetrics || {}),
    [config.cashControlTable],
  )

  let iFilter: any = null
  if (locationIds && locationIds.length > 0) {
    iFilter = { location_ids: [...locationIds], metrics }
  } else if (locationGroupIds && locationGroupIds.length > 0) {
    iFilter = { location_group_ids: [...locationGroupIds], metrics }
  }

  const { storeList, storeListLoading } = useStoreList()
  const { data: cashData, loading: cashDataLoading } =
    useQuery<IListLocationLossPreventionKpisType>(
      LIST_LOCATION_LOSS_PREVENTION_KPIS,
      {
        variables: {
          iFilter,
          iStartDate: startDate,
          iEndDate: endDate,
          useMetrics: metrics.length > 0,
        },
        skip: !startDate || !endDate || !iFilter,
      },
    )

  const { data: metricData, loading: metricDataLoading } = useQuery(
    LIST_LOCATION_METRIC_VALUES,
    {
      variables: {
        iLocationGroupIds: locationGroupIds,
        iMetricCodes: ['pnl_cash_over/short', 'net_sales'],
        iStartDate: startDate,
        iEndDate: endDate,
      },
      skip: !startDate || !endDate || !iFilter || !getColorFromValue,
    },
  )

  const metricDataNodes: {
    locationId: number
    metricData: { [key: string]: { value: number } }
  }[] = metricData?.listLocationMetricValues?.nodes ?? []
  const cashLocationLoading =
    cashDataLoading || metricDataLoading || storeListLoading

  const cashLocationData = useMemo(() => {
    const rawData = cashData?.listLocationLossPreventionKpis?.nodes || []
    const rawDataFromMetrics = cashData?.listLocationMetricValues?.nodes

    const tableData =
      rawData?.map((row: IListLocationLossPreventionKpisNodeType) => {
        const storeId = row.locationInfo.id
        const metricDataNode = metricDataNodes.find(
          ({ locationId }) => storeId === locationId,
        )
        const rawDataFromMetric = rawDataFromMetrics?.find(
          ({ locationId }) => storeId === locationId,
        )
        const storeCode = row.locationInfo.code
        const storeName = row.locationInfo.name

        const store = storeList?.listLocationDetails?.nodes.find(
          ({ id }) => storeId === id,
        )

        const cashOverOrShortAmount =
          metricDataNode?.metricData?.['pnl_cash_over/short']?.value
        const netSalesAmount = metricDataNode?.metricData?.['net_sales']?.value
        const cashOverOrShort = {
          count: row?.inaccurateCashierShiftCount,
          amount: getColorFromValue
            ? cashOverOrShortAmount
            : row?.cashOverOrShort,
          checkPercent:
            getColorFromValue && cashOverOrShortAmount && netSalesAmount
              ? {
                  value: (100 * cashOverOrShortAmount) / netSalesAmount,
                  color: getColorFromValue(
                    'cashovershort',
                    (100 * cashOverOrShortAmount) / netSalesAmount,
                  ),
                }
              : row?.inaccurateCashierShiftPercent,
        }

        const customData = row?.lossPreventionItems?.reduce(
          (result: ICustomDataType, itemData: ILossPreventionItems) => ({
            ...result,
            [itemData.type]: {
              count: itemData?.count,
              amount: itemData?.amount,
              checkPercent: getColorFromValue
                ? {
                    value: itemData?.checkPercent,
                    color: getColorFromValue(
                      itemData.type,
                      itemData?.checkPercent,
                    ),
                  }
                : itemData?.checkPercent,
            },
          }),
          {} as ICustomDataType,
        )
        const rowData = {
          storeId,
          storeCode,
          storeName,
          directorId: store?.directorEmployeeInfo?.id || 0,
          directorName: [
            store?.directorEmployeeInfo?.preferredName || '',
            store?.directorEmployeeInfo?.familyName || '',
          ]
            .filter(Boolean)
            .join(' '),
          cashOverOrShort,
          ...customData,
        }

        if (config.cashControlTable.cashControlDataFromMetrics)
          Object.entries(
            config.cashControlTable.cashControlDataFromMetrics,
          ).forEach(([key, value]) => {
            _.set(rowData, key, rawDataFromMetric?.metricData?.[value]?.value)
          })

        return rowData
      }) || []

    return tableData
  }, [cashData, metricData, locationGroupIds, startDate, endDate])

  return { cashLocationData, cashLocationLoading }
}

export default useLocationCash
